import { useCallback, useRef, useState } from 'react'
import { configuredFieldsRecordContext } from '../../utils/configuredFields/recordValues'
import type { ConfiguredFieldsFormValues } from '../../utils/configuredFields/types'
import { createConfiguredFieldsSession } from '../../utils/configuredFields/utils'
import type { WorkflowRecord } from '../../types'
import type {
  WorkflowAction,
  WorkflowActionSession,
  UseWorkflowActionsOptions,
} from './types'

/**
 * Guards lifecycle submissions and retains the closed session so its drawer
 * can animate out. Each opening initializes a fresh form session.
 * The action type preserves the matching destinations in submission callbacks.
 */
export function useWorkflowActions<Action extends WorkflowAction>({
  onSubmit,
  onSuccess,
}: UseWorkflowActionsOptions<Action>) {
  const submitting = useRef(false)
  const lastSessionNumber = useRef(0)
  const [pending, setPending] = useState(false)
  const [session, setSession] = useState<WorkflowActionSession<Action> | null>(
    null
  )
  const [open, setOpen] = useState(false)
  const onAction = useCallback(
    (record: WorkflowRecord, action: Action) => {
      if (
        submitting.current ||
        open ||
        !action.authorized ||
        action.disabledReason
      )
        return
      const context = configuredFieldsRecordContext(record)
      lastSessionNumber.current += 1
      setSession({
        fieldSession: createConfiguredFieldsSession({
          fields: action.fields,
          ...context,
        }),
        number: lastSessionNumber.current,
        target: {
          action,
          recordId: record.id,
          recordName: record.recordName,
        },
      })
      setOpen(true)
    },
    [open]
  )
  const close = useCallback(() => {
    if (!submitting.current) setOpen(false)
  }, [])
  const submitSession = useCallback(
    async ({ values }: ConfiguredFieldsFormValues): Promise<boolean> => {
      if (!session || !open || submitting.current) return false
      submitting.current = true
      setPending(true)
      try {
        const result = await onSubmit({
          recordId: session.target.recordId,
          target: session.target.action.target,
          values,
        })
        setOpen(false)
        try {
          await onSuccess(result, session.target)
        } catch {
          // Refresh errors must not offer another submission of a committed action.
        }
        return true
      } catch {
        // The mutation's standard error handler presents the error; retain the form for retry.
        return false
      } finally {
        submitting.current = false
        setPending(false)
      }
    },
    [onSubmit, onSuccess, session, open]
  )
  return { close, onAction, open, pending, session, submitSession }
}
